import React, { useEffect, useMemo, useState } from 'react'
import { Divider, TextField, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import { useAppSelector } from 'store/hooks'
import { getTopicIcon, getUnitName } from 'common/tenders/tenders.functions'
import { type Topic } from 'features/bids/bid.types'
import { formatNumberNoDec } from 'common/utils/functions'
import SimpleTooltip from 'common/ui/simple-tooltip'
import SimpleDropdown from 'common/ui/simple-dropdown'
import { ProposalStatus, UnitType } from 'common/tenders/tenders.types'

import styles from './ProposalTopicsGrid.module.scss'

const rowHeight = 58 // Default row height in MUI DataGrid
const headerHeight = 45 // Default header height in MUI DataGrid
interface ProposalTopicsGridProps {
  errorGrid?: string
  topicsArray: Topic[]
  setTopicsArray: React.Dispatch<React.SetStateAction<Topic[]>>
}

const ProposalTopicsGrid: React.FC<ProposalTopicsGridProps> = ({
  errorGrid,
  topicsArray,
  setTopicsArray,
}) => {
  const { palette } = useTheme()
  const { proposal } = useAppSelector((state) => state.bid)

  const mergedTopics = proposal?.tender?.topics?.map((tenderTopic) => {
    const foundTopic = topicsArray.find((topic) => tenderTopic.topicId === topic.topicId)
    if (foundTopic) {
      return {
        ...tenderTopic,
        unit: foundTopic.unit ?? { type: '', count: '', price: '' },
      }
    }
    return {
      ...tenderTopic,
      unit: { type: '', count: '', price: '' },
    }
  })

  useEffect(() => {
    if (proposal?.topics) {
      setTopicsArray(proposal.topics)
    }
  }, [proposal?.topics])

  const renderEditableCell = (params: GridRenderCellParams): JSX.Element => {
    const displayValue =
      !params.value || Number.isNaN(params.value)
        ? ''
        : params.field === 'unit.price'
        ? formatNumberNoDec(params.value)
        : params.value
    const showPlaceholder = !params.value || Number.isNaN(params.value)
    const getTablePlaceholder = (field: any): string => {
      switch (field) {
        case showPlaceholder && 'unit.count':
          return 'Unesite broj jedinica'
        case showPlaceholder && 'unit.price':
          return 'Unesite cenu jedinice'
        default:
          return ''
      }
    }
    const [isFocused, setIsFocused] = useState(false)

    const handleValueChange = (params: GridRenderCellParams, newValue: any): void => {
      const fieldParts = params.field.split('.')
      const updatedRows = mergedTopics?.map((row) => {
        if (row.topicId === params.id) {
          if (fieldParts.length === 2) {
            const key = fieldParts[0] as keyof typeof row
            if (typeof row[key] === 'object' && row[key] !== null) {
              return {
                ...row,
                [key]: {
                  ...(row[key] as object),
                  [fieldParts[1]]: newValue,
                },
              }
            }
          } else {
            return { ...row, [params.field]: newValue }
          }
        }
        return row
      })
      setTopicsArray(updatedRows as Topic[])
    }

    if (params.field === 'unit.type') {
      return (
        <SimpleDropdown
          selectedOption={params.value}
          handleChange={(newValue) => {
            handleValueChange(params, newValue)
          }}
          sx={{
            width: '100%',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '16px',
            textAlign: 'center',
            color: palette.white.main,
            backgroundColor: palette.blue600.main,
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: palette.blue600.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.blue600.main,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.blue600.main,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.blue600.main,
            },
            '& .MuiInputBase-input': {
              WebkitTextFillColor: palette.white.main,
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: palette.white.main,
            },
            '.MuiSvgIcon-root': {
              fill: 'white !important',
            },
          }}
          disabled={proposal?.status === ProposalStatus.SUBMITTED}
          options={[
            { id: UnitType.MAN_DAY, name: getUnitName(UnitType.MAN_DAY) },
            { id: UnitType.MAN_HOUR, name: getUnitName(UnitType.MAN_HOUR) },
            { id: UnitType.ITEM, name: getUnitName(UnitType.ITEM) },
          ]}
        />
      )
    }

    return (
      <div className={styles.textContainer}>
        <TextField
          type="text"
          disabled={proposal?.status === ProposalStatus.SUBMITTED}
          variant="standard"
          placeholder={isFocused ? '' : getTablePlaceholder(params.field)}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={() => {
            setIsFocused(false)
          }}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: palette.white.main,
              fontWeight: 400,
            },
          }}
          inputProps={{
            style: {
              textAlign: 'center',
            },
          }}
          InputProps={{
            disableUnderline: true,
            style: {
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '16px',
            },
          }}
          value={displayValue}
          onChange={(event) => {
            handleValueChange(params, parseFloat(event.target.value.replaceAll('.', '')))
          }}
        />
        {(params.field === 'unit.price' || params.field === 'totalPrice') && !!params.value && (
          <span>RSD</span>
        )}
      </div>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerAlign: 'center',
      headerName: 'Oblast rada',
      flex: 150,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <div className={styles.topicName}>
          <div style={{ marginRight: '10px' }}>{getTopicIcon(params.row.type)}</div>
          <SimpleTooltip title={params.value ?? '-'} className={styles.tooltip}>
            <span className={styles.truncate}>{params.value}</span>
          </SimpleTooltip>
        </div>
      ),
    },
    {
      field: 'unit.type',
      headerAlign: 'center',
      headerName: 'Jedinica',
      flex: 150,
      sortable: false,
      align: 'center',
      renderCell: renderEditableCell,
    },
    {
      field: 'unit.count',
      headerAlign: 'center',
      headerName: 'Broj jedinica',
      flex: 120,
      sortable: false,
      align: 'center',
      renderCell: renderEditableCell,
    },
    {
      field: 'unit.price',
      headerAlign: 'center',
      headerName: 'Cena po jedinici',
      flex: 120,
      sortable: false,
      align: 'center',
      renderCell: renderEditableCell,
    },
    {
      field: 'totalPrice',
      headerAlign: 'center',
      headerName: 'Ukupno',
      flex: 120,
      sortable: false,
      align: 'center',
      renderCell: (params) => {
        const unitPrice = parseFloat(params.row['unit.price']) ?? 0
        const unitCount = parseFloat(params.row['unit.count']) ?? 0
        return (
          <Typography variant="title3" sx={{ color: palette.charts.purple100.main }}>
            {unitPrice && unitCount ? formatNumberNoDec(unitPrice * unitCount).toString() + ' RSD' : '/'}
          </Typography>
        )
      },
    },
  ]
  const flatTopics =
    mergedTopics?.map((topic) => ({
      ...topic,
      'unit.type': topic.unit?.type,
      'unit.count': topic.unit?.count,
      'unit.price': topic.unit?.price,
    })) ?? []

  const tableHeight = flatTopics.length * rowHeight + headerHeight

  const totalPriceSum = useMemo(() => {
    return flatTopics.reduce((sum, row) => {
      const unitCount = row['unit.count'] ? Number(row['unit.count']) : 0
      const unitPrice = row['unit.price'] ? Number(row['unit.price']) : 0
      return sum + unitCount * unitPrice
    }, 0)
  }, [flatTopics])

  return (
    <div className={styles.container}>
      <div className={styles.budget}>
        <div className={styles.textWrapper}>
          <Typography variant="title1" sx={{ marginBottom: '7px', color: palette.white.main }}>
            Budžet za tender
          </Typography>
          <Typography variant="subtitle1">
            U tabeli ispod ispišite za svaku stavku Vaš budžet i ponudu
          </Typography>
        </div>
        <Divider className={styles.divider} flexItem />
        <div className={styles.dataGrid}>
          <div className={styles.grid} style={{ height: tableHeight }}>
            <DataGrid
              rows={flatTopics}
              columns={columns}
              getRowId={(row) => row.topicId}
              disableColumnSelector={true}
              disableRowSelectionOnClick={true}
              sx={{
                width: '100%',
                '& .MuiDataGrid-columnHeader': {
                  color: palette.blue100.main,
                  '& .MuiDataGrid-menuIcon': {
                    display: 'none',
                  },
                  pointerEvents: 'auto',
                },
                '& .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'initial',
                  '& .MuiDataGrid-cell': {
                    color: palette.white.main,
                  },
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'none',
                },
              }}
            />
          </div>
          <div className={styles.totalPriceSum}>
            <Typography className={styles.truncate} variant="title3" sx={{ color: palette.white.main }}>
              {formatNumberNoDec(totalPriceSum)} RSD
            </Typography>
          </div>
        </div>
        {errorGrid && (
          <Typography
            variant="subtitle1"
            sx={{ color: palette.charts.red.main, width: '100%', textAlign: 'center', paddingBottom: '10px' }}
          >
            {errorGrid}
          </Typography>
        )}
      </div>
    </div>
  )
}

export default ProposalTopicsGrid
